import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthFooter, OnboardingStepper } from 'components';
import FullPageCardContainer from 'components/FullPageCardContainer/FullPageCardContainer';
import OnboardingBrandInformationPage from 'pages/Onboarding/onboardingBrandInformationPage';
import OnboardingThemeCustomizationPage from 'pages/Onboarding/onboardingThemeCustomizationPage';
import OnboardingWelcomePage from 'pages/Onboarding/onboardingWelcomePage';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLogout } from 'api/auth/hooks';
import * as Roles from 'constants/roles';
import { DASHBOARD } from 'constants/routes';
import OnboardingFinalSplashPage from 'pages/Onboarding/onboardingFinalSplashPage';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { getCurrentOnboardingStep, setCurrentOnboardingStep } from '../../utils/tokenService';
import OnboardingRegionPage from './onBoardingRegionPage';

const ONBOARDING_COMPONENTS = [
  OnboardingWelcomePage,
  OnboardingBrandInformationPage,
  OnboardingThemeCustomizationPage,
  OnboardingRegionPage,
  OnboardingFinalSplashPage,
];

const ONBOARDING_STEPS = {
  WELCOME: 0,
  BRAND_INFORMATION: 1,
  THEME_CUSTOMIZATION: 2,
  REGION: 3,
  FINAL_SPLASH: 4,
};

const getValidOnboardingStep = (step) => {
  const parsedStep = Number.parseInt(step, 10);

  return parsedStep >= ONBOARDING_STEPS.WELCOME && parsedStep < ONBOARDING_COMPONENTS.length
    ? parsedStep
    : ONBOARDING_STEPS.WELCOME;
};

const getOnboardingStepFromBrand = (brand) => {
  if (brand?.theme?.uid || brand?.theme?.id) {
    return ONBOARDING_STEPS.REGION;
  }

  if (brand?.uid) {
    return ONBOARDING_STEPS.THEME_CUSTOMIZATION;
  }

  return ONBOARDING_STEPS.WELCOME;
};

const getInitialOnboardingStep = (adminUid, brand) => {
  const savedStep = getValidOnboardingStep(getCurrentOnboardingStep(adminUid));
  const brandStep = getOnboardingStepFromBrand(brand);

  return Math.max(savedStep, brandStep);
};

const OnboardingPage = () => {
  const { t } = useTranslation(['pages']);
  const { mutate: logoutUser } = useLogout();
  const role = useAuthStore((state) => state.role);
  const adminUid = useAuthStore((state) => state.user?.admin_details?.uid);
  const brand = useAuthStore((state) => state.user?.admin_details?.brand);
  const isOnboardingProcessFinished = useAuthStore(
    (state) => state.user?.admin_details?.brand?.is_onboarding_process_finished,
  );
  const [currentStep, setCurrentStep] = useState(() => getInitialOnboardingStep(adminUid, brand));

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const nextStep = Math.min(prev + 1, ONBOARDING_COMPONENTS.length - 1);

      setCurrentOnboardingStep(nextStep, adminUid);

      return nextStep;
    });
  }, [adminUid]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => {
      const nextStep = Math.max(ONBOARDING_STEPS.WELCOME, prev - 1);

      setCurrentOnboardingStep(nextStep, adminUid);

      return nextStep;
    });
  }, [adminUid]);

  const steps = useMemo(
    () =>
      ONBOARDING_COMPONENTS.map((component, index) => ({
        component,
        onNext: index < ONBOARDING_COMPONENTS.length - 1 ? goToNextStep : undefined,
        onPrevious: index > 0 ? goToPreviousStep : undefined,
      })),
    [goToNextStep, goToPreviousStep],
  );

  const CurrentStepComponent = steps[currentStep]?.component;
  const isAdmin = role === Roles.ADMIN;
  const isFinalStep = currentStep === steps.length - 1;

  useEffect(() => {
    setCurrentOnboardingStep(currentStep, adminUid);
  }, [currentStep, adminUid]);

  useEffect(() => {
    const nextStep = getInitialOnboardingStep(adminUid, brand);

    if (nextStep > currentStep) {
      setCurrentStep(nextStep);
      setCurrentOnboardingStep(nextStep, adminUid);
    }
  }, [adminUid, brand, currentStep]);

  if (!isAdmin || (isOnboardingProcessFinished && !isFinalStep)) {
    return <Navigate to={DASHBOARD} replace />;
  }

  return (
    <FullPageCardContainer
      lg="8"
      footer={AuthFooter}
      variant="onboarding"
      cornerAction={{ label: t('onboarding.logout'), icon: 'logout', onClick: logoutUser }}
    >
      <Helmet>
        <title>{t('onboarding.pageTitle')}</title>
      </Helmet>
      {currentStep > 0 && <OnboardingStepper currentStep={currentStep} totalSteps={steps.length} />}
      <CurrentStepComponent
        onNext={steps[currentStep]?.onNext}
        onPrevious={steps[currentStep]?.onPrevious}
      />
    </FullPageCardContainer>
  );
};

export default OnboardingPage;
